import { RotateCwIcon } from 'lucide-react'

import { Button } from '@/components/ui'
import { useScreeningLimits } from '@/modules/tools/bankruptcy/hooks'
import type { FilingDetail } from '@/modules/tools/bankruptcy/stores/types'
import { NeedMore } from '../NeedMore'
import { ScreeningBreakdown } from './ScreeningBreakdown'
import { ScreeningResults } from './ScreeningResults'

interface ReviewResultsProps {
  filing: FilingDetail
  onRescreen: () => void
  canRescreen: boolean
  isBusy: boolean
}

export const ReviewResults = ({
  filing,
  onRescreen,
  canRescreen,
  isBusy,
}: ReviewResultsProps) => {
  const limits = useScreeningLimits(filing)
  const overPageLimit = !!limits?.overPageLimit
  const outOfCredits = limits?.outOfCredits
  const blocked = overPageLimit || outOfCredits

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-lg">Review results</h2>
          <p className="text-muted-foreground text-sm">
            What the last screen found in this case.
          </p>
        </div>
        {canRescreen && (
          <Button
            theme="outline"
            size="sm"
            onClick={onRescreen}
            isLoading={isBusy}
            disabled={isBusy || blocked}
            loadingText="Starting..."
          >
            <RotateCwIcon className="h-3.5 w-3.5" />
            Re-screen
          </Button>
        )}
      </div>

      {canRescreen && overPageLimit && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-700 text-sm">
          This case is over its {filing.pageLimit}-page limit, so it can't be
          re-screened. Remove a file first. <NeedMore limit="pageCap" />
        </p>
      )}
      {canRescreen && outOfCredits && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-700 text-sm">
          You're out of screening credits, so this case can't be re-screened
          yet. <NeedMore limit="allowance" />
        </p>
      )}

      <ScreeningResults filing={filing} />
      <ScreeningBreakdown filing={filing} />
    </section>
  )
}
